import { MonoLabel } from '@/design-system';
import type { NhgClassification, Urgency } from '@/entities/finding';
import type { PhotoRecord } from '@/shared/offline';

import { surveyCopy } from '../copy';
import type { AnswerKey, NodeId } from '../engine';
import type { OpenSharedQuestions } from '../hooks/useOpenSharedQuestions';
import type { QuestionFinding, QuestionView } from '../model/view';
import type { StreamEntry, StreamHeading } from '../model/stream';
import { MultiCountCard } from './MultiCountCard';
import { QuestionCard } from './QuestionCard';

export interface SurveyStreamProps {
    entries: readonly StreamEntry[];
    photosByQuestion: ReadonlyMap<AnswerKey, readonly PhotoRecord[]>;
    notes: ReadonlyMap<AnswerKey, string>;
    questionFindings: ReadonlyMap<AnswerKey, QuestionFinding>;
    /** Het bouwjaar uit de BAG, als hint bij de bouwperiodevraag. */
    buildYear: number | null;
    shared: OpenSharedQuestions;
    readOnly: boolean;
    onToggle: (view: QuestionView, optionId: number, selected: boolean) => void;
    onNoteChange: (key: AnswerKey, text: string) => void;
    onLocationChange: (key: AnswerKey, text: string) => void;
    /** `prompted`: de urgentie die een keuze zonder vast bedrag noemt. */
    onAmountChange: (key: AnswerKey, cents: number | null, prompted?: Urgency) => void;
    onUrgencyChange: (key: AnswerKey, urgency: Urgency) => void;
    onNhgChange: (key: AnswerKey, nhg: NhgClassification | null) => void;
    onValueCommit: (view: QuestionView, raw: string) => void;
    onAddPhotos: (view: QuestionView, files: File[]) => void;
    onRemovePhoto: (clientUuid: string) => void;
    onMultiCountChange: (nodeId: NodeId, count: number) => void;
}

/**
 * Eén hoofdonderdeel als doorlopende stroom: vraag lezen, antwoord aantikken,
 * eventueel inspreken, foto erbij, door naar de volgende - zonder tussenschermen.
 *
 * De subonderdelen zijn rustige tussenkoppen die meescrollen; ze staan niet vast
 * boven in beeld, want elke vastgezette regel kost hoogte die de inspecteur aan
 * vragen wil besteden. Een vervolgvraag verschijnt onder de vraag die hem opent,
 * dus de plek in beeld verspringt niet.
 */
export function SurveyStream({
    entries,
    photosByQuestion,
    notes,
    questionFindings,
    buildYear,
    shared,
    readOnly,
    onToggle,
    onNoteChange,
    onLocationChange,
    onAmountChange,
    onUrgencyChange,
    onNhgChange,
    onValueCommit,
    onAddPhotos,
    onRemovePhoto,
    onMultiCountChange,
}: SurveyStreamProps) {
    if (entries.length === 0) {
        return (
            <p className="m-0 text-[13px] text-[var(--text-tertiary-safe)]">
                {surveyCopy.item.empty}
            </p>
        );
    }

    return (
        <div className="flex flex-col">
            {entries.map((entry) => {
                if (entry.kind === 'heading') {
                    return <SubHeading key={`kop-${entry.nodeId}`} heading={entry} />;
                }

                if (entry.kind === 'count') {
                    return (
                        <div
                            key={`aantal-${entry.nodeId}`}
                            data-node={entry.nodeId}
                            className="scroll-mt-14"
                        >
                            <MultiCountCard
                                label={entry.label}
                                count={entry.count}
                                answered={entry.answered}
                                readOnly={readOnly}
                                onChange={(count) => onMultiCountChange(entry.nodeId, count)}
                            />
                        </div>
                    );
                }

                const view = entry.view;

                /*
                 * A question that belongs to another main part and already has its
                 * answer is not drawn again here. It used to sit at the top of every
                 * part that shares it, asking to be changed; the answer is given, and
                 * the part it belongs to is where it is corrected. Still open, and the
                 * hook keeps it on screen — asked here once, and staying until "Klaar"
                 * so nothing collapses under the finger.
                 */
                if (view.askedIn && !shared.isOpen(view.key)) {
                    return null;
                }

                return (
                    <QuestionCard
                        key={view.key}
                        view={view}
                        shared={
                            view.askedIn
                                ? {
                                      place: view.askedIn.place,
                                      onDone: view.answered
                                          ? () => shared.close(view.key)
                                          : undefined,
                                  }
                                : undefined
                        }
                        photos={photosByQuestion.get(view.key) ?? []}
                        note={notes.get(view.key) ?? ''}
                        finding={questionFindings.get(view.key) ?? null}
                        buildYear={buildYear}
                        onToggle={(optionId, selected) => onToggle(view, optionId, selected)}
                        onNoteChange={(text) => onNoteChange(view.key, text)}
                        onLocationChange={(text) => onLocationChange(view.key, text)}
                        onAmountChange={(cents, prompted) =>
                            onAmountChange(view.key, cents, prompted)
                        }
                        onUrgencyChange={(urgency) => onUrgencyChange(view.key, urgency)}
                        onNhgChange={(nhg) => onNhgChange(view.key, nhg)}
                        onValueCommit={(raw) => onValueCommit(view, raw)}
                        onAddPhotos={(files) => onAddPhotos(view, files)}
                        onRemovePhoto={onRemovePhoto}
                        readOnly={readOnly}
                    />
                );
            })}
        </div>
    );
}

/**
 * De tussenkop van een subonderdeel: nummer, naam, een haarlijn en de teller van
 * dat onderdeel. Klein en rustig - hij wijst de weg, hij is geen scherm.
 */
function SubHeading({ heading }: { heading: StreamHeading }) {
    return (
        <div
            data-node={heading.nodeId}
            className="flex scroll-mt-14 items-center gap-2 pt-2 pb-0 first:pt-0"
            style={{ paddingInlineStart: (heading.depth - 1) * 10 }}
        >
            <MonoLabel size={10} variant="value">
                {heading.number}
            </MonoLabel>
            <h2 className="m-0 font-[family-name:var(--font-display)] text-[13px] leading-[1.25] font-semibold text-[var(--text-secondary)]">
                {heading.name}
            </h2>
            <span aria-hidden="true" className="h-px flex-1 bg-[var(--border-subtle)]" />
            {heading.visible > 0 ? (
                <MonoLabel
                    size={10}
                    variant="value"
                    aria-label={surveyCopy.item.subPartCount(heading.answered, heading.visible)}
                >
                    {heading.answered}/{heading.visible}
                </MonoLabel>
            ) : null}
        </div>
    );
}
